import { type Theme } from "@material-ui/core/styles";
import { dark, light, type Themes } from "@saleor/macaw-ui";
import { themes, vars } from "@saleor/macaw-ui-next";

const breakpoints = {
  keys: ["xs", "sm", "md", "lg", "xl"],
  values: { lg: 1680, md: 1280, sm: 600, xl: 1920, xs: 0 },
} as unknown as Theme["breakpoints"];

const brand = {
  primary: "#4f46e5",
  primaryHover: "#4338ca",
  primaryActive: "#3730a3",
  primarySoft: "#eef2ff",
  primarySoftHover: "#e0e7ff",
  primarySoftBorder: "#c7d2fe",
  primaryBorderSubtle: "#e0e7ff",
  primaryDeep: "#3730a3",
  primaryRing: "rgba(79, 70, 229, 0.18)",
  primaryDisabledBg: "#c7d2fe",
  primaryDisabledText: "#a5b4fc",
  danger: "#dc2626",
  dangerHover: "#b91c1c",
  dangerActive: "#991b1b",
  dangerSoft: "#fef2f2",
  dangerSoftHover: "#fee2e2",
  dangerBorderSubtle: "#fecaca",
  dangerRing: "rgba(220, 38, 38, 0.18)",
};

export const themeOverrides: Partial<Theme> = {
  breakpoints,
  overrides: {
    MuiCard: {
      root: {
        border: 0,
        backgroundColor: vars.colors.background.default1,
      },
    },
    MuiTypography: {
      body2: {
        fontSize: "13px",
      },
      caption: {
        fontSize: "13px",
        fontWeight: 500,
      },
    },
    MuiTextField: {
      root: {
        backgroundColor: vars.colors.background.default1,
      },
    },
    MuiInputBase: {
      root: {
        borderColor: vars.colors.border.default1,
      },
    },
    MuiOutlinedInput: {
      root: {
        backgroundColor: vars.colors.background.default1,
        color: vars.colors.text.default1,
        "&:hover $notchedOutline": {
          borderColor: vars.colors.border.default1,
        },
        "&:hover": {
          boxShadow: "none !important",
        },
      },
      notchedOutline: {
        borderColor: vars.colors.border.default1,
      },
    },
    MuiInputLabel: {
      root: {
        color: vars.colors.text.default1,
      },
    },
    MuiCardHeader: {
      root: {
        paddingRight: vars.spacing[8],
        backgroundColor: vars.colors.background.default1,
      },
      title: {
        fontSize: vars.fontSize[5],
        // MUI it strictly typed to be a number, but we are passing CSS variable here
        fontWeight: vars.fontWeight.medium as unknown as number,
        lineHeight: vars.lineHeight[5],
        letterSpacing: vars.letterSpacing[5],
      },
    },
    MuiCardContent: {
      root: {
        backgroundColor: vars.colors.background.default1,
      },
    },
    MuiPaper: {
      root: {
        backgroundColor: vars.colors.background.default1,
        color: vars.colors.text.default1,
      },
    },
    MuiTable: {
      root: {
        tableLayout: "auto !important" as "auto",
      },
    },
    MuiTableCell: {
      root: {
        borderBottomColor: vars.colors.border.default1,
      },
    },
    MuiIconButton: {
      edgeEnd: {
        marginRight: 0,
      },
    },
    MuiCardActions: {
      root: {
        backgroundColor: vars.colors.background.default1,
        paddingLeft: 0,
        paddingRight: 0,
      },
    },
    MuiFormHelperText: {
      root: {
        backgroundColor: vars.colors.background.default1,
      },
    },
    MuiDialogTitle: {
      root: {
        fontSize: vars.fontSize[5],
        // MUI it strictly typed to be a number, but we are passing CSS variable here
        fontWeight: vars.fontWeight.medium as unknown as number,
        lineHeight: vars.lineHeight[5],
        letterSpacing: vars.letterSpacing[5],
      },
    },
    MuiButton: {
      root: {
        textTransform: "none",
        fontWeight: 500,
        letterSpacing: 0,
        transition:
          "background-color 120ms ease, color 120ms ease, border-color 120ms ease, box-shadow 120ms ease",
      },
      containedPrimary: {
        backgroundColor: brand.primary,
        color: "#ffffff",
        boxShadow: "none",
        "&:hover": {
          backgroundColor: brand.primaryHover,
          boxShadow: "none",
        },
        "&:active": {
          backgroundColor: brand.primaryActive,
          boxShadow: "none",
        },
        "&.Mui-focusVisible": {
          backgroundColor: brand.primaryHover,
          boxShadow: `0 0 0 3px ${brand.primaryRing}`,
        },
        "&.Mui-disabled": {
          backgroundColor: brand.primaryDisabledBg,
          color: "#ffffff",
        },
      },
      outlinedPrimary: {
        color: brand.primaryDeep,
        borderColor: brand.primarySoftBorder,
        backgroundColor: "#ffffff",
        "&:hover": {
          backgroundColor: brand.primarySoft,
          borderColor: brand.primary,
        },
        "&:active": {
          backgroundColor: brand.primarySoftHover,
          borderColor: brand.primaryActive,
        },
        "&.Mui-focusVisible": {
          borderColor: brand.primary,
          boxShadow: `0 0 0 3px ${brand.primaryRing}`,
        },
        "&.Mui-disabled": {
          color: brand.primaryDisabledText,
          borderColor: brand.primaryBorderSubtle,
        },
      },
      textPrimary: {
        color: brand.primary,
        "&:hover": {
          backgroundColor: brand.primarySoft,
        },
        "&:active": {
          backgroundColor: brand.primarySoftHover,
        },
        "&.Mui-focusVisible": {
          boxShadow: `0 0 0 3px ${brand.primaryRing}`,
        },
      },
    },
    MuiCheckbox: {
      colorPrimary: {
        "&$checked": {
          color: brand.primary,
        },
        "&.Mui-checked": {
          color: brand.primary,
        },
      },
    },
    MuiRadio: {
      colorPrimary: {
        "&$checked": {
          color: brand.primary,
        },
        "&.Mui-checked": {
          color: brand.primary,
        },
      },
    },
    MuiSwitch: {
      colorPrimary: {
        "&$checked": {
          color: brand.primary,
        },
        "&$checked + $track": {
          backgroundColor: brand.primary,
        },
        "&.Mui-checked": {
          color: brand.primary,
        },
        "&.Mui-checked + .MuiSwitch-track": {
          backgroundColor: brand.primary,
        },
      },
    },
    MuiTab: {
      textColorPrimary: {
        "&$selected": {
          color: brand.primary,
        },
        "&.Mui-selected": {
          color: brand.primary,
        },
      },
    },
    MuiTabs: {
      indicator: {
        backgroundColor: brand.primary,
      },
    },
    MuiLinearProgress: {
      colorPrimary: {
        backgroundColor: brand.primarySoft,
      },
      barColorPrimary: {
        backgroundColor: brand.primary,
      },
    },
    MuiCircularProgress: {
      colorPrimary: {
        color: brand.primary,
      },
    },
  },
};

export const paletteOverrides: Themes = {
  light: {
    ...light,
    background: {
      ...light.background,
      default: themes.defaultLight.colors.background.default1,
    },
  },
  dark: {
    ...dark,
    background: {
      ...dark.background,
      default: themes.defaultDark.colors.background.default1,
    },
  },
};
